import {useState, type FocusEvent, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column} from '@/components/data-table'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {FieldError} from '@/components/field-error'
import {Hint} from '@/components/ui/hint'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {
  SOLICITUDES_SST_PAGE_SIZE,
  useSolicitudesSstAdmin,
  usePatchSolicitudSst,
  type SolicitudSstAdmin,
} from '@/hooks/inci/consola/use-solicitud-sst'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'

const dateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

function DescartarDialog({target, onClose}: {target: SolicitudSstAdmin | null; onClose: () => void}) {
  const {t} = useTranslation()
  const patch = usePatchSolicitudSst()
  const [form, setForm] = useState({motivo: '', error: null as string | null})
  const [motivoError, setMotivoError] = useState<string | undefined>()

  const dismiss = () => {
    setForm({motivo: '', error: null})
    setMotivoError(undefined)
    patch.reset()
    onClose()
  }

  function blurMotivo(e: FocusEvent<HTMLTextAreaElement>) {
    setMotivoError(validateRequired(e.target.value) ?? undefined)
  }

  const descartar = (e: FormEvent) => {
    e.preventDefault()
    const motivo = form.motivo.trim()
    if (!target) return
    const err = validateRequired(motivo)
    setMotivoError(err ?? undefined)
    if (err) return
    patch.mutate(
      {solicitudId: target.id, action: 'descartar', motivo},
      {onSuccess: dismiss, onError: (err) => setForm((prev) => ({...prev, error: translateApiError(err)}))}
    )
  }

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && dismiss()}>
      <DialogContent data-testid="sst-descartar-dialog">
        <DialogHeader>
          <DialogTitle>{t('solicitudesSst.admin.descartarTitle', {name: target?.business_name ?? ''})}</DialogTitle>
        </DialogHeader>
        <form onSubmit={descartar} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="sst-descartar-motivo">{t('solicitudesSst.admin.motivoLabel')}</Label>
            <Textarea
              id="sst-descartar-motivo"
              data-testid="sst-descartar-motivo"
              value={form.motivo}
              onChange={(e) => {
                setForm({motivo: e.target.value, error: null})
                setMotivoError(undefined)
              }}
              onBlur={blurMotivo}
              maxLength={300}
              minRows={3}
              aria-invalid={!!motivoError}
            />
            <FieldError error={motivoError} />
            <Hint>{t('solicitudesSst.admin.motivoHint')}</Hint>
          </div>
          <FormDialogFooter
            error={form.error}
            pending={patch.isPending}
            submitLabel={t('solicitudesSst.admin.descartarSubmit')}
            onCancel={dismiss}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

const estadoTone: Record<SolicitudSstAdmin['estado'], 'amber' | 'green' | 'neutral'> = {
  pendiente: 'amber',
  atendida: 'green',
  descartada: 'neutral',
}

export function SolicitudesSstSection() {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data} = useSolicitudesSstAdmin(page, true)
  const patch = usePatchSolicitudSst()
  const [descartando, setDescartando] = useState<SolicitudSstAdmin | null>(null)
  const [accionError, setAccionError] = useState<string | null>(null)

  const rows = data?.items ?? []

  // Clic sin diálogo: sin esta superficie el fallo es invisible (react-query se queda el rechazo).
  function atender(row: SolicitudSstAdmin) {
    setAccionError(null)
    patch.mutate({solicitudId: row.id, action: 'atender'}, {onError: (err) => setAccionError(translateApiError(err))})
  }

  const columns: Column<SolicitudSstAdmin>[] = [
    {
      key: 'empresa',
      header: t('solicitudesSst.admin.table.empresa'),
      cell: (row) => (
        <span className="block min-w-0">
          <span className="block truncate font-medium text-foreground">{row.business_name}</span>
          <span className="block truncate font-mono text-xs text-muted-foreground">{row.business_nit}</span>
        </span>
      ),
    },
    {
      key: 'contacto',
      header: t('solicitudesSst.admin.table.contacto'),
      cell: (row) => (
        <span className="block min-w-0 text-xs text-muted-foreground">
          <span className="block truncate">{row.business_email ?? '—'}</span>
          {row.business_phone && <span className="block truncate font-mono">{row.business_phone}</span>}
        </span>
      ),
    },
    {
      key: 'fecha',
      header: t('solicitudesSst.admin.table.fecha'),
      width: 'w-28',
      cell: (row) => (
        <span className="block font-mono text-xs text-muted-foreground">
          {dateFormat.format(new Date(row.created_at))}
        </span>
      ),
    },
    {
      key: 'nota',
      header: t('solicitudesSst.admin.table.nota'),
      cell: (row) => <span className="block truncate text-xs text-muted-foreground">{row.nota ?? '—'}</span>,
    },
    {
      key: 'estado',
      header: t('solicitudesSst.admin.table.estado'),
      width: 'w-40',
      className: 'text-right',
      cell: (row) =>
        row.estado === 'pendiente' ? (
          <div className="flex flex-wrap items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              data-testid={`sst-atender-${row.id}`}
              disabled={patch.isPending}
              onClick={() => atender(row)}>
              {t('solicitudesSst.admin.atender')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              data-testid={`sst-descartar-${row.id}`}
              onClick={() => setDescartando(row)}>
              {t('solicitudesSst.admin.descartar')}
            </Button>
          </div>
        ) : (
          <span className="flex justify-end">
            <Tag tone={estadoTone[row.estado]}>{t(`solicitudesSst.admin.estado.${row.estado}`)}</Tag>
          </span>
        ),
    },
  ]

  return (
    <>
      <ListCard
        testId="solicitudes-sst-section"
        toolbar={
          accionError ? (
            <p className="text-sm text-destructive" role="alert" data-testid="sst-accion-error">
              {accionError}
            </p>
          ) : undefined
        }
        footer={
          data &&
          data.total > SOLICITUDES_SST_PAGE_SIZE && (
            <PaginationBar page={page} pageSize={SOLICITUDES_SST_PAGE_SIZE} total={data.total} onPageChange={setPage} />
          )
        }>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          emptyState={<ListEmptyState page title={t('solicitudesSst.admin.emptyTitle')} />}
        />
      </ListCard>

      <DescartarDialog target={descartando} onClose={() => setDescartando(null)} />
    </>
  )
}
